import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { HighlightUrlContext } from './context';
import type { HighlightUrlConfigItem, HighlightUrlMatcher, HighlightUrlValue } from './types';

const isUrlMatch = (matcher: HighlightUrlMatcher, pathname: string) => {
  if (typeof matcher === 'function') return matcher(pathname);

  if (matcher instanceof RegExp) return matcher.test(pathname);

  const urls = Array.isArray(matcher) ? matcher : [matcher];

  return urls.some(url => {
    if (!url) return false;
    if (url === '*') return true;
    return pathname === url || pathname.startsWith(`${url}/`);
  });
};

export const HighlightUrlProvider = ({
  children,
  config,
  enabled = true,
}: {
  children: ReactNode;
  config: HighlightUrlConfigItem[];
  enabled?: boolean;
}) => {
  const [values, setValues] = useState<Record<string, HighlightUrlValue>>({});
  const hasLoaded = useRef(false);

  const loadValue = useCallback(async (configItem: HighlightUrlConfigItem) => {
    try {
      const value = Boolean(await configItem.getValue());
      setValues(previous => ({ ...previous, [configItem.id]: value }));
      return value;
    } catch {
      setValues(previous => ({ ...previous, [configItem.id]: false }));
      return false;
    }
  }, []);

  const reload = useCallback(
    async (id: string) => {
      const configItem = config.find(candidate => candidate.id === id);
      if (!configItem) return false;
      return loadValue(configItem);
    },
    [config, loadValue],
  );

  const reloadAll = useCallback(async () => {
    await Promise.all(config.map(loadValue));
  }, [config, loadValue]);

  useEffect(() => {
    if (!enabled || hasLoaded.current) return;

    hasLoaded.current = true;
    void reloadAll();
  }, [enabled, reloadAll]);

  const getHighlightById = useCallback(
    (id: string) => {
      const configItem = config.find(candidate => candidate.id === id);
      if (!configItem) return false;

      return values[configItem.id] === true;
    },
    [config, values],
  );

  const hasHighlightUrl = useCallback(
    (url?: string) => {
      if (!url) return false;

      const configItem = config.find(candidate => isUrlMatch(candidate.url, url));
      if (!configItem) return false;

      return values[configItem.id] === true;
    },
    [config, values],
  );

  const value = useMemo(
    () => ({
      values,
      hasHighlightUrl,
      getHighlightById,
      reload,
      reloadAll,
    }),
    [values, hasHighlightUrl, getHighlightById, reload, reloadAll],
  );

  return <HighlightUrlContext.Provider value={value}>{children}</HighlightUrlContext.Provider>;
};
